import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

/**
 * Unified controller for all element bars in Plantafel
 * Handles click events to open element show pages in sidedrawer
 *
 * Replaces:
 * - plantafel_task_controller.js (for non-task elements)
 * - plantafel_month_item_controller.js
 */
export default class extends Controller {
  static values = {
    id: Number,
    type: String, // 'task', 'appointment', 'sick_note', 'leave_request', 'employee'
  }

  connect() {
    this.element.addEventListener("click", this.handleClick.bind(this))
    this.element.addEventListener("contextmenu", this.handleContextMenu.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleClick.bind(this))
    this.element.removeEventListener("contextmenu", this.handleContextMenu.bind(this))
    this.closeContextMenu()
  }

  handleClick(event) {
    // Don't open sidedrawer if we just finished resizing
    if (document.body.dataset.plantafelResizing === "true") {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    // Don't open if clicking on resize handles
    if (
      event.target.closest('[data-plantafel-resize-target="leftHandle"]') ||
      event.target.closest('[data-plantafel-resize-target="rightHandle"]')
    ) {
      return
    }

    // Don't open if clicking on drag handle
    if (event.target.closest('[data-plantafel-task-drag-target="handle"]')) {
      return
    }

    // Don't navigate if clicking on a resource badge (for tasks)
    if (event.target.closest("[data-resource-assignment-id]")) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (!this.idValue || !this.typeValue) {
      Honeybadger.notify("Missing id or type for plantafel element", {
        context: { level: "warning", component: "plantafel-element" },
      })
      return
    }

    this.openInSidedrawer()
  }

  buildShowUrl() {
    const basePaths = {
      task: "/tasks",
      sick_note: "/sick_notes",
      leave_request: "/leave_requests",
      appointment: "/appointments",
      employee: "/employees",
    }

    const basePath = basePaths[this.typeValue]
    if (!basePath) {
      Honeybadger.notify(`Unknown plantafel element type: ${this.typeValue}`, {
        context: { level: "warning", component: "plantafel-element" },
      })
      return null
    }

    return `${basePath}/${this.idValue}?source=plantafel`
  }

  buildEditUrl() {
    const basePaths = {
      task: "/tasks",
      sick_note: "/sick_notes",
      leave_request: "/leave_requests",
      appointment: "/appointments",
      employee: "/employees",
    }

    const basePath = basePaths[this.typeValue]
    if (!basePath) return null

    return `${basePath}/${this.idValue}/edit?source=plantafel`
  }

  openInSidedrawer() {
    const url = this.buildShowUrl()
    if (!url) return

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-element", action: "open_sidedrawer" },
        })
        // Fallback to regular navigation
        window.location.href = url
      })
  }

  openEditInSidedrawer() {
    const url = this.buildEditUrl()
    if (!url) return

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-element", action: "open_edit" },
        })
        window.location.href = url
      })
  }

  handleContextMenu(event) {
    event.preventDefault()
    this.closeContextMenu()

    // Create context menu
    const menu = document.createElement("div")
    menu.id = "plantafel-context-menu"
    menu.className =
      "fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]"
    menu.style.left = `${event.clientX}px`
    menu.style.top = `${event.clientY}px`

    const label = this.getTypeLabel()
    const viewText = this.getTranslation("contextView", "%{label} anzeigen").replace(
      "%{label}",
      label,
    )
    const editText = this.getTranslation("contextEdit", "%{label} bearbeiten").replace(
      "%{label}",
      label,
    )

    menu.innerHTML = `
      <button type="button" class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" data-action="view">
        ${viewText}
      </button>
      <button type="button" class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" data-action="edit">
        ${editText}
      </button>
    `

    // Add click handlers
    menu.querySelector('[data-action="view"]').addEventListener("click", () => {
      this.closeContextMenu()
      this.openInSidedrawer()
    })

    menu.querySelector('[data-action="edit"]').addEventListener("click", () => {
      this.closeContextMenu()
      this.openEditInSidedrawer()
    })

    document.body.appendChild(menu)

    // Close on click outside
    document.addEventListener("click", this.closeContextMenu.bind(this), { once: true })
  }

  closeContextMenu() {
    const menu = document.getElementById("plantafel-context-menu")
    if (menu) {
      menu.remove()
    }
  }

  // Get translation from the main plantafel controller's data attributes
  getTranslation(key, fallback) {
    const plantafelContainer = document.querySelector('[data-controller*="plantafel"]')
    if (plantafelContainer) {
      const value =
        plantafelContainer.dataset[`plantafel${key.charAt(0).toUpperCase() + key.slice(1)}Value`]
      if (value) return value
    }
    return fallback
  }

  // Get the localized label for the element type
  getTypeLabel() {
    const typeKeyMap = {
      task: "contextTask",
      appointment: "contextAppointment",
      sick_note: "contextSickNote",
      leave_request: "contextLeaveRequest",
      employee: "contextEmployee",
    }
    const fallbackLabels = {
      task: "Task",
      appointment: "Termin",
      sick_note: "Krankmeldung",
      leave_request: "Urlaub",
      employee: "Mitarbeiter",
    }
    const key = typeKeyMap[this.typeValue]
    return this.getTranslation(key, fallbackLabels[this.typeValue] || "Element")
  }
}
